import { getDeviceId } from '@/lib/device';
import { readFirstTouch, type FirstTouch } from '@/lib/first-touch';

export interface SyncUserData {
  firebaseUid: string;
  email: string | null;
  name: string | null;
  authProvider: 'GOOGLE' | 'EMAIL';
  /**
   * Signup-consent capture (fast-follow): explicit marketing-email choice from the
   * register form's checkbox. Undefined for every OTHER caller (login, the
   * onAuthStateChanged listener's own retry sync when no registration is in flight,
   * Google sign-in) — the backend only acts on this for a BRAND NEW user (see
   * auth.controller.ts's syncUser), never for an existing user's login.
   */
  marketingConsent?: boolean;
  /**
   * Self-reported birth YEAR (not a full date — 2026-09-28 marketing-consent
   * plan). Only ever sent alongside `marketingConsent` from the register
   * form's revealed birth-year field; the backend validates range and acts
   * on it only for a brand-new user, same scoping as marketingConsent.
   */
  birthYear?: number;
  /**
   * Explicit "this is a fresh ToS/Privacy Policy acceptance" signal from the
   * register form's required checkbox. Only sent (and only acted on) for a
   * brand-new user — see auth.controller.ts's LegalAcceptance write.
   */
  agreeTerms?: boolean;
  /**
   * First-touch attribution from the aks_ft cookie. Attached by syncUser itself (callers
   * never set it); the backend only persists it for a brand-new user.
   */
  firstTouch?: FirstTouch;
}

export interface UserFromBackend {
  id: string;
  name: string | null;
  avatarUrl: string | null;
  role: 'STUDENT' | 'ADMIN' | 'INSTRUCTOR';
  onboardingCompleted?: boolean;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'DELETED';
  deletionScheduledAt?: string | null;
  /**
   * Present only when a granted:true marketingConsent choice this call made
   * was refused because the user could not be confirmed an adult. The
   * account is still created/synced successfully — the register form uses
   * this to show the "not available for your age" placeholder instead of a
   * generic error.
   */
  marketingConsentRefusalReason?: 'NOT_ADULT';
}

// Backend codes where the caller MUST know sync failed — the alternative (silently
// returning null) lets registerWithEmail sail past a half-created account: Firebase user
// exists, backend User row doesn't, every subsequent authenticated call 401s forever
// (see requireAuth.ts). Anything NOT in this set is treated as transient/non-fatal by
// design (network hiccup, backend blip) — onAuthStateChanged already retries syncUser on
// every auth-state change, so swallowing those and falling back to an optimistic user is
// intentional, not the bug this set exists to fix.
const FATAL_SYNC_ERROR_CODES = new Set(['REGISTRATION_DISABLED', 'EMAIL_SIGNUP_UNAVAILABLE', 'EMAIL_REQUIRED']);

/**
 * Every code in FATAL_SYNC_ERROR_CODES is thrown ONLY from inside the backend's `if
 * (!user)` branch (auth.controller.ts syncUser) — i.e. only when the backend has just
 * PROVEN no User row exists and is refusing to create one. That's the narrow guarantee
 * AuthContext's registerWithEmail relies on to decide whether it's safe to delete the
 * just-created Firebase account: exported here so that decision reuses this exact set
 * instead of re-deriving (and risking drifting from) it.
 */
export function isFatalSyncErrorCode(code: unknown): boolean {
  return typeof code === 'string' && FATAL_SYNC_ERROR_CODES.has(code);
}

// Best-effort: a missing/unreadable cookie just means no attribution, never a failed sync.
function withFirstTouch(userData: SyncUserData): SyncUserData {
  const firstTouch = readFirstTouch();
  return firstTouch ? { ...userData, firstTouch } : userData;
}

export const AuthService = {
  async fetchMe(token: string): Promise<UserFromBackend | null> {
    try {
      const response = await fetch(`/api/proxy/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user data from backend:', error);
      throw error;
    }
  },

  async syncUser(token: string, userData: SyncUserData): Promise<UserFromBackend | null> {
    try {
      const response = await fetch(`/api/proxy/auth/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Device-Id': getDeviceId(),
        },
        body: JSON.stringify(withFirstTouch(userData)),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (FATAL_SYNC_ERROR_CODES.has(data.error)) {
          throw Object.assign(new Error(data.message || 'Gagal memproses akun.'), {
            code: data.error,
          });
        }
        return null;
      }
      return await response.json();
    } catch (error) {
      const code = error instanceof Error ? (error as { code?: string }).code : undefined;
      if (code && FATAL_SYNC_ERROR_CODES.has(code)) throw error;
      console.error('Error syncing user:', error);
      return null;
    }
  }
};
